// @ts-nocheck
import useInfinteScroll from "@store/Framework/Common/Hooks/useInfiniteScroll";
import useLocalizeCurrency from "@store/Framework/Common/Hooks/useLocalizeCurrency";
import useReportApi from "@store/Framework/Common/Hooks/useReportAPI/useReportAPI";
import useSearch from "@store/Framework/Common/Hooks/useSearch";
import { expenditureByManufacturerExport } from "@store/Framework/Common/Utilities/excelExportFunctions";
import {
    ExpendituresByManufacturersItemModel,
    ExpendituresByManufacturersModel,
} from "@store/Framework/Services/Reporting/ReportingModels";
import { isExpendituresByManufacturersModel } from "@store/Framework/Services/Reporting/ReportingTypeGuards";
import { reportParametersSelector } from "@store/Framework/Store/Bunzl/Pages/Reporting/ReportOrderFrequency/ReportOrderFrequencySelectors";
import useClear from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useClear";
import { CustomWidgetModule, ProductDataLineItems, Products } from "@store/Framework/Types/types";
import AccordionLayout from "@store/Library/Components/Reporting/Common/AccordionLayout";
import ApiButtons from "@store/Library/Components/Reporting/Common/ApiButtons";
import ButtonExportFile from "@store/Library/Components/Reporting/Common/ButtonExportFile";
import ProductSection from "@store/Library/Components/Reporting/Common/ProductSection";
import ReportContainer from "@store/Library/Components/Reporting/Common/ReportContainer";
import ReportingFilterGridItem from "@store/Library/Components/Reporting/Common/ReportingFilterGridItem";
import ReportingSearchBox from "@store/Library/Components/Reporting/Common/ReportingSearchBox";
import { LocationPicker } from "@store/Library/Components/Reporting/LocationPicker/LocationPicker";
import ManufacturerPicker from "@store/Library/Components/Reporting/ManufacturerPicker/ManufacturerPicker";
import { getFixedProducts, productionOverrideStyles } from "@store/Library/Widgets/Reports/ExpenditureByProductReport";
import { ExportButtonGridItem } from "@store/Library/Widgets/Reports/InvoiceDetailSearchForm";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { useWebsite } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

const headers = ["Item Number", "Description", "UOM", "Quantity", "Cost"];
const dataKeys: (keyof ProductDataLineItems)[] = ["itemNumber", "itemDesc", "UAM", "quantity", "totcost"];
const getProducts = (data: ExpendituresByManufacturersItemModel[]) => {
    const products: Products = {};
    data.forEach(item => {
        const { itemnumber, itemdesc, unitofmeasure, totquantity, totcost } = item;
        item.vendorname = item.vendorname.includes("WESCLEAN") ? "BUNZL REGARD" : item.vendorname;
        if (!products[item.vendorname]) {
            products[item.vendorname] = {
                data: [],
                totCost: item.totcost,
                totQuantity: item.totquantity,
            };
        } else {
            products[item.vendorname].totCost += totcost;
            products[item.vendorname].totQuantity += totquantity;
        }
        products[item.vendorname].data.push({
            itemNumber: itemnumber,
            itemDesc: itemdesc,
            UAM: unitofmeasure,
            quantity: totquantity,
            totcost,
        });
    });
    return products;
};

const DatePicker = getWidgetComponent("Reports/ReportSearchFieldDateRange");

const ExpenditureByManufacturerReport: React.FC = () => {
    const { isError, isLoading, updateData } = useReportApi("expendituresByManufacturers");
    const [searchTerm, setSearchTerm] = useSearch(() => setPage(1));
    const [selectedManufacturers, setSelectedManufacturers] = useState<string[]>([]);
    const [expenditureByManufacturersData, setExpenditureByManufacturersData] = useState<Products>({});
    const reportParameters = useSelector(reportParametersSelector);
    const { customers } = reportParameters;
    const [startInfiniteScroll, setStartInfiniteScroll] = React.useState(false);
    const { itemsToShow, setPage } = useInfinteScroll(10, startInfiniteScroll);
    const productPrefix = `${useWebsite().name.toUpperCase()}-`;
    const localizeCurrency = useLocalizeCurrency();
    const onclear = useClear();
    const handleClear = () => {
        setSelectedManufacturers([]);
        onclear();
        setExpenditureByManufacturersData({});
        setPage(1);
        setStartInfiniteScroll(false);
    };

    const handleDataUpdate = async () => {
        const data = await updateData({
            ...reportParameters,
            manufacturers: selectedManufacturers,
        });
        if (isExpendituresByManufacturersModel(data)) {
            const fixedData = await getFixedProducts<ExpendituresByManufacturersModel>(data, productPrefix);
            setExpenditureByManufacturersData(getProducts(fixedData.items));
            setStartInfiniteScroll(true);
            setPage(1);
        }
    };
    const filteredData = useMemo(() => {
        const filteredData: Products = {};
        if (!searchTerm) {
            return expenditureByManufacturersData;
        }
        Object.keys(expenditureByManufacturersData).forEach(manufacturer => {
            const data = expenditureByManufacturersData[manufacturer].data.filter(item => {
                return (
                    item.itemDesc?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    item.itemNumber?.includes(searchTerm)
                );
            });
            if (data.length) {
                filteredData[manufacturer] = {
                    data,
                    totCost: expenditureByManufacturersData[manufacturer].totCost,
                    totQuantity: expenditureByManufacturersData[manufacturer].totQuantity,
                };
            }
        });
        return filteredData;
    }, [expenditureByManufacturersData, searchTerm]);

    return (
        <ReportContainer>
            <AccordionLayout>
                <ReportingFilterGridItem doubleSized={true}>
                    <DatePicker />
                </ReportingFilterGridItem>
                <ReportingFilterGridItem>
                    <LocationPicker isError={isError && customers.length === 0} />
                </ReportingFilterGridItem>
                <ReportingFilterGridItem>
                    <ManufacturerPicker
                        isError={isError && selectedManufacturers.length === 0}
                        setSelectedManufacturers={setSelectedManufacturers}
                    />
                </ReportingFilterGridItem>
                <ApiButtons onClear={handleClear} updateData={handleDataUpdate} isLoading={isLoading} />
                <ReportingSearchBox
                    disabled={!startInfiniteScroll}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                />
            </AccordionLayout>
            <ExportButtonGridItem
                css={css`
                    justify-content: flex-end !important;
                `}
                width={12}
            >
                <ButtonExportFile
                    excelData={expenditureByManufacturerExport(expenditureByManufacturersData, headers, dataKeys)}
                    disabled={Object.keys(expenditureByManufacturersData).length === 0}
                />
            </ExportButtonGridItem>
            <>
                {Object.keys(filteredData).map((manufacturerName, index) => {
                    const data = filteredData[manufacturerName];
                    if (index > itemsToShow) {
                        return null;
                    }
                    return (
                        <ProductSection
                            headers={headers}
                            dataKeys={dataKeys}
                            key={manufacturerName}
                            productData={data}
                            extendedStyles={productionOverrideStyles}
                            formatDictionary={{ totcost: localizeCurrency }}
                            itemName={manufacturerName}
                        />
                    );
                })}
            </>
        </ReportContainer>
    );
};

const widgetModule: CustomWidgetModule = {
    component: ExpenditureByManufacturerReport,
    definition: {
        group: "Reporting",
        displayName: "Expenditure By Manufacturer Report",
    },
};
export default widgetModule;
